// Drawing Room Chat Panel - ephemeral chat with Pusher

interface ChatMessage {
  userId: number;
  userName: string;
  content: string;
  type: 'user' | 'system' | 'hint' | 'correct';
  timestamp: number;
}

type GuessCallback = (text: string) => void;

export class ChatPanel {
  private roomCode: string;
  private userId: number;
  private userName: string;
  private containerEl: HTMLElement | null = null;
  private messagesEl: HTMLElement | null = null;
  private inputEl: HTMLInputElement | null = null;
  private toggleBtn: HTMLElement | null = null;
  private badgeEl: HTMLElement | null = null;
  private isOpen = false;
  private channel: any = null;
  private guessCallback: GuessCallback | null = null;
  private unreadCount = 0;
  private csrfToken: string;

  constructor(roomCode: string, userId: number, userName: string) {
    this.roomCode = roomCode;
    this.userId = userId;
    this.userName = userName;
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    this.createUI();
  }

  bindToChannel(channel: any): void {
    if (!channel) return;
    this.channel = channel;

    channel.bind('chat-message', (data: ChatMessage) => {
      this.addMessage(data);
    });

    channel.bind('catch-mind-correct-guess', (data: any) => {
      this.addMessage({
        userId: 0,
        userName: 'system',
        content: `${data.guesserName}님이 정답을 맞혔습니다! (${data.word})`,
        type: 'correct',
        timestamp: Date.now()
      });
    });

    channel.bind('catch-mind-started', () => {
      this.addSystemMessage('캐치마인드 게임이 시작되었습니다!');
    });

    channel.bind('catch-mind-finished', () => {
      this.addSystemMessage('캐치마인드 게임이 종료되었습니다!');
    });

    // Presence events
    channel.bind('pusher:member_added', (member: any) => {
      const name = member.info?.name || `User ${member.id}`;
      this.addSystemMessage(`${name}님이 입장했습니다`);
    });

    channel.bind('pusher:member_removed', (member: any) => {
      const name = member.info?.name || `User ${member.id}`;
      this.addSystemMessage(`${name}님이 퇴장했습니다`);
    });
  }

  setGuessCallback(cb: GuessCallback | null): void {
    this.guessCallback = cb;
  }

  showHint(text: string): void {
    this.addMessage({
      userId: 0,
      userName: 'system',
      content: text,
      type: 'hint',
      timestamp: Date.now()
    });
  }

  private createUI(): void {
    // Toggle button in top bar
    this.toggleBtn = document.getElementById('chat-toggle-btn');
    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', () => this.toggle());
    }

    // Badge
    this.badgeEl = document.getElementById('chat-unread-badge');

    // Chat panel
    this.containerEl = document.createElement('div');
    this.containerEl.id = 'drawing-chat-panel';
    this.containerEl.className = 'fixed bottom-4 right-4 w-80 bg-gray-900/95 backdrop-blur border border-gray-700 rounded-xl shadow-2xl z-[10002] flex flex-col hidden';
    this.containerEl.style.height = '400px';
    this.containerEl.innerHTML = `
      <div class="flex items-center justify-between px-3 py-2 border-b border-gray-700">
        <span class="text-white text-sm font-bold">채팅</span>
        <button id="chat-close-btn" class="text-gray-400 hover:text-white transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      <div id="chat-messages" class="flex-1 overflow-y-auto p-2 space-y-1 text-sm"></div>
      <div class="border-t border-gray-700 p-2">
        <div class="flex space-x-2">
          <input id="chat-input" type="text" maxlength="200" placeholder="메시지를 입력하세요..."
            class="flex-1 bg-gray-800 text-white text-sm rounded-lg px-3 py-2 border border-gray-600 focus:border-purple-500 focus:outline-none"
            autocomplete="off" />
          <button id="chat-send-btn" class="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-lg transition-colors">
            전송
          </button>
        </div>
      </div>
    `;

    document.getElementById('drawing-app-container')?.appendChild(this.containerEl);

    this.messagesEl = this.containerEl.querySelector('#chat-messages');
    this.inputEl = this.containerEl.querySelector('#chat-input');

    this.containerEl.querySelector('#chat-close-btn')?.addEventListener('click', () => this.toggle());
    this.containerEl.querySelector('#chat-send-btn')?.addEventListener('click', () => this.sendMessage());

    this.inputEl?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.sendMessage();
      }
      // Prevent keyboard shortcuts from firing while typing
      e.stopPropagation();
    });
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.containerEl) {
      this.containerEl.classList.toggle('hidden', !this.isOpen);
    }
    if (this.isOpen) {
      this.unreadCount = 0;
      this.updateBadge();
      this.inputEl?.focus();
      this.scrollToBottom();
    }
  }

  private async sendMessage(): Promise<void> {
    if (!this.inputEl) return;
    const text = this.inputEl.value.trim();
    if (!text) return;

    this.inputEl.value = '';

    // If catch mind is active, also send as guess
    if (this.guessCallback) {
      this.guessCallback(text);
    }

    try {
      await fetch(`/drawing/room/${this.roomCode}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ message: text })
      });
    } catch (e) {
      console.error('[ChatPanel] Send failed:', e);
    }
  }

  private addMessage(msg: ChatMessage): void {
    if (!this.messagesEl) return;

    const div = document.createElement('div');

    switch (msg.type) {
      case 'system':
        div.className = 'text-gray-500 text-xs text-center py-0.5';
        div.textContent = msg.content;
        break;
      case 'hint':
        div.className = 'text-yellow-400 text-xs text-center py-0.5 font-medium';
        div.textContent = msg.content;
        break;
      case 'correct':
        div.className = 'text-green-400 text-xs text-center py-1 font-bold bg-green-900/30 rounded';
        div.textContent = msg.content;
        break;
      default: {
        const isMe = msg.userId === this.userId;
        div.className = 'py-0.5';
        const nameSpan = document.createElement('span');
        nameSpan.className = isMe ? 'text-purple-400 font-bold text-xs' : 'text-blue-400 font-bold text-xs';
        nameSpan.textContent = msg.userName;
        const contentSpan = document.createElement('span');
        contentSpan.className = 'text-gray-300 text-xs ml-1';
        contentSpan.textContent = msg.content;
        div.appendChild(nameSpan);
        div.appendChild(contentSpan);
        break;
      }
    }

    this.messagesEl.appendChild(div);

    // Limit messages
    while (this.messagesEl.children.length > 200) {
      this.messagesEl.removeChild(this.messagesEl.firstChild!);
    }

    this.scrollToBottom();

    // Unread badge
    if (!this.isOpen && msg.type === 'user' && msg.userId !== this.userId) {
      this.unreadCount++;
      this.updateBadge();
    }
  }

  addSystemMessage(content: string): void {
    this.addMessage({
      userId: 0,
      userName: 'system',
      content,
      type: 'system',
      timestamp: Date.now()
    });
  }

  private scrollToBottom(): void {
    if (this.messagesEl) {
      this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    }
  }

  private updateBadge(): void {
    if (!this.badgeEl) return;
    if (this.unreadCount > 0) {
      this.badgeEl.textContent = String(this.unreadCount > 99 ? '99+' : this.unreadCount);
      this.badgeEl.classList.remove('hidden');
    } else {
      this.badgeEl.classList.add('hidden');
    }
  }

  destroy(): void {
    if (this.channel) {
      this.channel.unbind('chat-message');
      this.channel.unbind('catch-mind-correct-guess');
      this.channel.unbind('catch-mind-started');
      this.channel.unbind('catch-mind-finished');
    }
    this.containerEl?.remove();
  }
}
